/* Shelf scroller — mobile-first horizontal carousel affordance for the
   "Similar games" / "Recently viewed" rows. Adds a draggable progress
   scrubber, an edge fade, and (desktop only) prev/next arrows. Everything is
   driven off the native scroll position so swipe, trackpad, scrubber and
   arrows stay in sync. The markup is server-rendered; this only enhances it.

   Perf: the hot scroll path is rAF-coalesced and reads no layout-triggering
   props (scrollWidth/clientWidth are cached and only re-measured on resize /
   image load). */
(function () {
  if (window.customElements && customElements.get('shelf-scroller')) return;

  var reduce = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  var EDGE = 8; // scroll-snap rests a few px off true 0 / max

  class ShelfScroller extends HTMLElement {
    connectedCallback() {
      this.shelf = this.querySelector('.shelf');
      if (!this.shelf || this._wired) return;
      this._wired = true;

      this.thumb = this.querySelector('.shelf-progress__thumb');
      this.track = this.querySelector('.shelf-progress');
      this.prevBtn = this.querySelector('[data-shelf-nav="prev"]');
      this.nextBtn = this.querySelector('[data-shelf-nav="next"]');

      this._max = 0;
      this._cw = 0;
      this._sw = 0;
      this._raf = 0;

      this._onScroll = this.onScroll.bind(this);
      this.shelf.addEventListener('scroll', this._onScroll, { passive: true });

      // Re-measure (layout reads) only when size can actually change.
      this._measure = this.measure.bind(this);
      if (window.ResizeObserver) {
        this._ro = new ResizeObserver(this._measure);
        this._ro.observe(this.shelf);
      } else {
        window.addEventListener('resize', this._measure);
      }

      // Box-art images change scrollWidth as they load.
      this.shelf.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', this._measure, { once: true });
      }, this);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.page.bind(this, -1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.page.bind(this, 1));
      if (this.track) this.track.addEventListener('pointerdown', this.scrub.bind(this));

      this.measure();
      // Placeholder cards render <svg> (no load event) — recompute once layout settles.
      var self = this;
      requestAnimationFrame(function () { self.measure(); });
    }

    disconnectedCallback() {
      if (this.shelf) this.shelf.removeEventListener('scroll', this._onScroll);
      if (this._ro) this._ro.disconnect();
      else window.removeEventListener('resize', this._measure);
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    // Expensive: cache the layout dimensions, then paint.
    measure() {
      this._cw = this.shelf.clientWidth;
      this._sw = this.shelf.scrollWidth;
      this._max = Math.max(0, this._sw - this._cw);
      this.render();
    }

    // Cheap: coalesce scroll bursts into one paint per frame.
    onScroll() {
      if (this._raf) return;
      var self = this;
      this._raf = requestAnimationFrame(function () {
        self._raf = 0;
        self.render();
      });
    }

    render() {
      var left = this.shelf.scrollLeft; // the only hot-path read
      var max = this._max;
      var overflow = max > EDGE;
      var canPrev = overflow && left > EDGE;
      var canNext = overflow && left < max - EDGE;

      this.toggleAttribute('data-overflow', overflow);
      this.dataset.canPrev = String(canPrev);
      this.dataset.canNext = String(canNext);

      if (this.prevBtn) this.prevBtn.disabled = !canPrev;
      if (this.nextBtn) this.nextBtn.disabled = !canNext;

      if (this.thumb && overflow && this._sw > 0) {
        var w = Math.max(this._cw / this._sw, 0.16); // keep the thumb grabbable
        var pos = max > 0 ? left / max : 0;
        this.thumb.style.width = w * 100 + '%';
        this.thumb.style.left = pos * (1 - w) * 100 + '%';
      }
    }

    page(dir) {
      var amount = Math.max(160, this._cw * 0.85);
      this.shelf.scrollBy({
        left: dir * amount,
        behavior: reduce.matches ? 'auto' : 'smooth'
      });
    }

    scrub(e) {
      if (e.button && e.button !== 0) return; // primary button / touch only
      var shelf = this.shelf;
      var track = this.track;
      var max = this._max;
      var rect = track.getBoundingClientRect();
      function to(x) {
        var f = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
        shelf.scrollLeft = f * max;
      }
      function move(ev) { to(ev.clientX); }
      function end() {
        track.removeEventListener('pointermove', move);
        track.removeEventListener('pointerup', end);
        track.removeEventListener('pointercancel', end);
      }
      // Capture so move/up/cancel still fire if the finger leaves the thin track.
      try { track.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      track.addEventListener('pointermove', move);
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      to(e.clientX);
      e.preventDefault();
    }
  }

  customElements.define('shelf-scroller', ShelfScroller);
})();
